import React, { isValidElement, type ReactElement, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import c from "classnames/bind";

import styles from "./grid.item.module.scss";

const cx = c.bind(styles);

export type ItemProps = Omit<BaseProps, "children"> & {
  children: ReactNode;
  /** The number of columns that the item spans on small screens. */
  colSpanSm?: 1 | 2 | 3 | 4;
  /** The number of columns that the item spans on medium screens. */
  colSpanMd?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /** The number of columns that the item spans on large screens. */
  colSpanLg?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  /** This will be passed from the parent component. */
  noColGap?: boolean;
};

const rootClassName = "purpur-grid-item";

export const Item = ({
  children,
  className,
  colSpanSm = 4,
  colSpanMd = 8,
  colSpanLg = 12,
  noColGap = false,
  ...props
}: ItemProps) => {
  const classes = cx([
    rootClassName,
    {
      [`${rootClassName}--no-col-gap`]: noColGap,
      [`${rootClassName}-colspan-sm-${colSpanSm}`]: colSpanSm,
      [`${rootClassName}-colspan-md-${colSpanMd}`]: colSpanMd,
      [`${rootClassName}-colspan-lg-${colSpanLg}`]: colSpanLg,
    },
    className,
  ]);

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export const isItem = (child?: ReactNode): child is ReactElement<ItemProps> =>
  !!child && isValidElement<ItemProps>(child) && child.type === Item;

Item.displayName = "Grid.Item";
